import React from 'react';

// Size/shape scale (see the "Size & shape scale" comment in index.css):
//   md control = inputs/selects + standard buttons: rounded-xl, 1px border, px-4/px-6 py-3, text-base, leading-6 -> 50px tall
//   sm control = compact inputs/selects + small buttons: rounded-lg, 1px border, px-3 py-2, text-sm, leading-5 -> 38px tall
//   xs button  = inline table/toolbar actions: rounded-md, 1px border, px-2 py-1, text-xs, leading-4 -> 26px tall
//                (min-h 32px below `sm` for touch; 26px from `sm` up)
//   touch targets below `sm`: segmented tabs min-h 38px; 16px icon-only table buttons (刪除這一列)
//                get `max-sm:-m-2 max-sm:p-2` -> 32x32 tap box with no layout shift.
// Responsive: phone-first base classes + `sm:` restoring the desktop value, so >= 640px renders as before.
//   shell p-4 -> sm:p-8, card body px-4 -> sm:px-6, title text-2xl -> sm:text-3xl.
//   Inputs/selects/textareas are forced to 16px below `sm` in index.css (iOS focus-zoom).
const inputBaseClasses = 'w-full border border-[var(--tgia-color-control-border)] bg-[var(--tgia-color-input)] text-[var(--tgia-color-text)] shadow-sm transition placeholder:text-[var(--tgia-color-placeholder)] focus:border-[var(--tgia-color-focus)] focus:ring-2 focus:ring-[var(--tgia-color-focus-ring)] focus:outline-none disabled:cursor-not-allowed disabled:bg-[var(--tgia-color-disabled-surface)] disabled:text-[var(--tgia-color-disabled-text)]';
const filledButtonFocusClasses = 'focus:outline-none focus:ring-2 focus:ring-[var(--tgia-color-focus)] focus:ring-offset-2 focus:ring-offset-[var(--tgia-color-surface)]';
const buttonMdClasses = 'inline-flex items-center justify-center gap-2 rounded-xl border px-6 py-3 text-base font-medium leading-6 transition';
const outlineButtonDisabledClasses = 'disabled:cursor-not-allowed disabled:border-[var(--tgia-color-disabled-border)] disabled:bg-[var(--tgia-color-disabled-surface)] disabled:text-[var(--tgia-color-disabled-text)]';

export const pageToneClasses = {
  page: 'min-h-screen [background:var(--tgia-page-bg)] py-4 px-4 sm:py-8',
  shell: 'relative mx-auto max-w-7xl rounded-[32px] border border-[var(--tgia-color-border)] [background:var(--tgia-shell-bg)] p-4 sm:p-8 shadow-[var(--tgia-shadow-shell)] backdrop-blur-sm',
  title: 'text-2xl font-bold text-[var(--tgia-color-title)] sm:text-3xl',
  subtitle: 'text-lg text-[var(--tgia-color-subtitle)] sm:text-xl',
  requiredNote: 'text-sm text-[var(--tgia-color-required-note)]'
};

export const inputToneClasses = {
  text: `${inputBaseClasses} rounded-xl px-4 py-3 text-base leading-6`,
  // select 會忽略 line-height，用 min-h 對齊同列的 input / 按鈕（50px / 38px）
  select: `${inputBaseClasses} min-h-[50px] rounded-xl px-4 py-3 text-base leading-6`,
  textarea: `${inputBaseClasses} rounded-xl px-4 py-3 text-base leading-6`,
  compactText: `${inputBaseClasses} rounded-lg px-3 py-2 text-sm leading-5`,
  compactSelect: `${inputBaseClasses} min-h-[38px] rounded-lg px-3 py-2 text-sm leading-5`,
  compactTextarea: `${inputBaseClasses} rounded-lg px-3 py-2 text-sm leading-5`
};

export const dropdownToneClasses = {
  panel: 'tgia-searchable-dropdown-panel absolute z-50 w-full mt-1 rounded-xl border border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface)] shadow-[var(--tgia-shadow-dropdown)] max-h-60 overflow-y-auto',
  option: 'tgia-searchable-dropdown-option px-3 py-2 cursor-pointer transition',
  optionHover: 'hover:bg-[var(--tgia-color-surface-muted)]',
  optionActive: 'tgia-searchable-dropdown-option--active bg-[var(--tgia-color-surface-active)]',
  optionSelected: 'tgia-searchable-dropdown-option--selected bg-[var(--tgia-color-surface-muted)] font-semibold',
  optionHidden: 'bg-[var(--tgia-color-caution-surface-muted)] border-l-4 border-[var(--tgia-color-caution)]',
  optionText: 'text-sm font-medium text-[var(--tgia-color-text)]',
  descriptionText: 'mt-1 text-xs text-[var(--tgia-color-text-muted)]',
  emptyText: 'px-3 py-2 text-sm text-[var(--tgia-color-text-muted)]'
};

export const badgeToneClasses = {
  brand: 'border border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-brand-surface)] text-[var(--tgia-color-brand-text)]',
  secondary: 'border border-[var(--tgia-color-brand)] bg-[var(--tgia-color-brand)] text-[var(--tgia-color-on-brand)]'
};

const sectionToneClasses = {
  surface: 'border-[var(--tgia-color-border)] bg-[var(--tgia-color-surface)] shadow-[var(--tgia-shadow-card)]',
  brand: 'border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-surface-soft)] shadow-[var(--tgia-shadow-card)]',
  subtle: 'border-[var(--tgia-color-border-subtle)] bg-[var(--tgia-color-surface-subtle)] shadow-sm',
  secondary: 'border-[var(--tgia-color-caution-border)] bg-[var(--tgia-color-caution-surface)] shadow-sm'
};

export const infoToneClasses = {
  info: 'tgia-info-panel tgia-info-panel--info border-[var(--tgia-color-brand-border)] bg-[var(--tgia-color-brand-surface)] text-[var(--tgia-color-text)]',
  success: 'tgia-info-panel tgia-info-panel--success border-[var(--tgia-color-success-border)] bg-[var(--tgia-color-success-surface)] text-[var(--tgia-color-success-text)]',
  error: 'tgia-info-panel tgia-info-panel--error border-[var(--tgia-color-error-border)] bg-[var(--tgia-color-error-surface)] text-[var(--tgia-color-error-text)]',
  caution_primary: 'tgia-info-panel tgia-info-panel--caution-primary border-[var(--tgia-color-caution-border)] bg-[var(--tgia-color-caution-surface)] text-[var(--tgia-color-caution-text)]',
  caution_secondary: 'tgia-info-panel tgia-info-panel--caution-secondary border-[var(--tgia-color-caution-border)] bg-[var(--tgia-color-caution-surface-muted)] text-[var(--tgia-color-caution-text-muted)]'
};

export const buttonToneClasses = {
  primary: `${buttonMdClasses} border-transparent text-[var(--tgia-color-on-brand)] shadow-sm ${filledButtonFocusClasses} disabled:cursor-not-allowed disabled:opacity-60 bg-[var(--tgia-color-brand)] hover:bg-[var(--tgia-color-brand-hover)]`,
  download_primary: `${buttonMdClasses} border-transparent text-[var(--tgia-color-download-text)] shadow-sm ${filledButtonFocusClasses} disabled:cursor-not-allowed disabled:opacity-60 bg-[var(--tgia-color-download-bg)] hover:bg-[var(--tgia-color-download-bg-hover)] hover:text-[var(--tgia-color-download-text-hover)]`,
  neutral: `${buttonMdClasses} text-[var(--tgia-color-neutral-text)] border-[var(--tgia-color-neutral-border)] bg-[var(--tgia-color-neutral-bg)] hover:bg-[var(--tgia-color-neutral-bg-hover)] hover:text-[var(--tgia-color-neutral-text-hover)] ${filledButtonFocusClasses} disabled:cursor-not-allowed disabled:opacity-60`,
  other: `text-[var(--tgia-color-caution)] border-[var(--tgia-color-caution)] bg-transparent hover:bg-[var(--tgia-color-caution)] hover:text-[var(--tgia-color-on-filled)] ${outlineButtonDisabledClasses}`,
  upload: 'text-[var(--tgia-color-upload)] border-[var(--tgia-color-upload)] bg-transparent hover:bg-[var(--tgia-color-upload)] hover:text-[var(--tgia-color-on-filled)]',
  // download_secondary: 'text-[var(--tgia-color-brand-text)] border-[var(--tgia-color-brand)] bg-transparent hover:bg-[var(--tgia-color-brand)] hover:text-[var(--tgia-color-on-brand)]',
  excel: 'text-[var(--tgia-color-success-text)] border-[var(--tgia-color-success-text)] bg-transparent hover:bg-[var(--tgia-color-success-text)] hover:text-[var(--tgia-color-on-success)]',
  add: 'text-[var(--tgia-color-upload)] border-[var(--tgia-color-upload)] bg-transparent hover:bg-[var(--tgia-color-upload)] hover:text-[var(--tgia-color-on-filled)]',
  clear: `text-[var(--tgia-color-danger)] border-[var(--tgia-color-danger)] bg-transparent hover:bg-[var(--tgia-color-danger)] hover:text-[var(--tgia-color-on-filled)] ${outlineButtonDisabledClasses}`
};

// Size + shape + focus for the outline tones above (other/upload/excel/add/clear), which only carry colors.
// Usage: `${buttonSizeClasses.sm} ${buttonToneClasses.upload}`.
export const buttonSizeClasses = {
  sm: `inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium leading-5 transition max-sm:whitespace-nowrap ${filledButtonFocusClasses}`,
  xs: `inline-flex items-center justify-center gap-1 rounded-md border px-2 py-1 text-xs font-medium max-sm:min-h-[32px] max-sm:whitespace-nowrap leading-4 transition ${filledButtonFocusClasses}`
};

export const apPackageToneClasses = {
  accent: 'text-[var(--tgia-color-ap-accent)]',
  lockedInput: 'cursor-not-allowed font-bold text-[var(--tgia-color-ap-locked-text)] bg-[var(--tgia-color-ap-locked-surface)] border-[var(--tgia-color-ap-locked-border)]'
};

export const segmentedTabToneClasses = {
  root: 'relative isolate pt-3 sm:pt-4',
  stack: 'relative overflow-visible',
  tabList: 'relative flex w-fit items-end gap-1 pl-0 mb-[-2px]',
  tab: 'relative inline-flex shrink-0 min-w-[5.25rem] max-sm:min-h-[38px] items-center justify-center rounded-t-[14px] border border-[var(--tgia-color-border)] px-2.5 py-2 text-[11px] font-semibold leading-none tracking-[0.05em] transition duration-200 ease-out transform-gpu focus:outline-none focus:ring-2 focus:ring-[var(--tgia-color-focus)] focus:ring-offset-2 focus:ring-offset-[var(--tgia-color-surface)] disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[6.75rem] sm:px-3.5 sm:text-[12px]',
  active: 'tgia-segmented-tab--active z-30 -mb-px translate-x-[1px] translate-y-[-2px] scale-[1.02] border-b-[var(--tgia-color-tab-active-border)] bg-[var(--tgia-color-surface)] text-[var(--tgia-color-text)] shadow-[var(--tgia-shadow-tab-active)]',
  inactive: 'tgia-segmented-tab--inactive z-10 translate-y-[3px] scale-[0.98] border-b-[var(--tgia-color-tab-inactive-border)] bg-[var(--tgia-color-tab-inactive)] text-[var(--tgia-color-tab-inactive-text)] shadow-[var(--tgia-shadow-tab-inactive)] hover:translate-y-[1px] hover:bg-[var(--tgia-color-tab-hover)] hover:text-[var(--tgia-color-tab-inactive-text)]',
  panel: 'tgia-segmented-panel relative z-20 overflow-hidden rounded-[28px] rounded-tl-none border border-[var(--tgia-color-border)] [background:var(--tgia-panel-bg)] shadow-[var(--tgia-shadow-panel)] backdrop-blur-sm',
  panelBody: 'px-4 pb-5 pt-6 sm:px-8 sm:pb-7 sm:pt-8'
};

export const SectionCard = ({
  title,
  subtitle,
  badge,
  badgeTone = 'brand',
  tone = 'surface',
  headerAlign = 'left',
  badgeAlign = 'center',
  icon,
  actions,
  footer,
  className = '',
  bodyClassName = '',
  children
}) => {
  const toneClasses = sectionToneClasses[tone] || sectionToneClasses.surface;
  const isCenteredHeader = headerAlign === 'center';
  const titleTextClass = tone === 'secondary' ? 'text-[var(--tgia-color-caution-text)]' : 'text-[var(--tgia-color-text)]';
  const subtitleTextClass = tone === 'secondary' ? 'text-[var(--tgia-color-caution-text-muted)]' : 'text-[var(--tgia-color-text-muted)]';

  return (
    <section className={`overflow-hidden rounded-2xl border ${toneClasses} ${className}`}>
      {(title || subtitle || badge || actions) && (
        <div className={`px-4 pt-5 pb-4 sm:px-6 sm:pt-6 ${isCenteredHeader ? 'flex flex-col items-center text-center gap-1' : 'flex flex-col items-start gap-4 sm:flex-row sm:justify-between'}`}>
          <div className={`${isCenteredHeader ? 'w-full space-y-1' : 'space-y-1'}`}>
            {badge && (
              <div className={isCenteredHeader
                ? `mb-1 flex w-full ${badgeAlign === 'start' ? 'justify-start' : 'justify-center'}`
                : ''
              }>
                <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide ${badgeToneClasses[badgeTone] || badgeToneClasses.brand}`}>
                  {badge}
                </div>
              </div>
            )}
            {title && (
              <h3 className={`text-xl font-bold ${titleTextClass} ${isCenteredHeader ? 'text-center' : ''}`}>
                {icon && <span className="mr-2 inline-flex align-middle">{icon}</span>}
                {title}
              </h3>
            )}
            {subtitle && <p className={`text-sm leading-6 ${subtitleTextClass} ${isCenteredHeader ? 'text-center' : ''}`}>{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}

      <div className={`px-4 pb-5 sm:px-6 sm:pb-6 ${bodyClassName}`}>
        {children}
      </div>

      {footer && (
        <div className="border-t border-[var(--tgia-color-border-subtle)] px-4 py-4 sm:px-6">
          {footer}
        </div>
      )}
    </section>
  );
};

export const InfoPanel = ({
  title,
  children,
  tone = 'info',
  icon,
  className = '',
  titleClassName = '',
  bodyClassName = ''
}) => {
  const toneClasses = infoToneClasses[tone] || infoToneClasses.info;

  return (
    <div className={`rounded-xl border px-4 py-4 ${toneClasses} ${className}`}>
      <div className="flex items-start gap-3">
        {icon && (
          <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--tgia-color-icon-surface)] text-current shadow-sm">
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          {title && (
            <p className={`mb-2 text-sm font-semibold ${titleClassName}`}>
              {title}
            </p>
          )}
          <div className={`text-sm leading-6 text-inherit ${bodyClassName}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ActionBar = ({
  left,
  center,
  right,
  className = '',
  divider = true
}) => {
  return (
    // Phones: left (sized to content) + right (fills the rest) share the first row; center takes its own full-width row below.
    // >= sm: one row, three columns (left | center | right) as on desktop.
    <div
      className={`grid grid-cols-[auto_minmax(0,1fr)] gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] max-sm:[&_button]:whitespace-nowrap max-sm:[&_button]:px-4 ${divider ? 'border-t border-[var(--tgia-color-border-subtle)] pt-5' : ''} ${className}`}
    >
      <div className="flex items-center justify-start gap-3">
        {left}
      </div>
      <div className="order-last col-span-2 flex items-center justify-center empty:hidden [&>*]:w-full sm:order-none sm:col-span-1 sm:empty:flex sm:[&>*]:w-auto">
        {center}
      </div>
      <div className="flex items-center justify-end gap-3 [&>*]:flex-1 sm:[&>*]:flex-initial">
        {right}
      </div>
    </div>
  );
};
